import { Dialog, DialogActions, DialogContent, Button } from "@mui/material";
import React, { useEffect, useState, useMemo } from "react";
import {
  setRecomandModal,
  setSelectedAd,
} from "../../../redux/slices/LcaSlice";
import { useDispatch, useSelector } from "react-redux";
import { fetchBWV } from "../Data";
import Select from "react-select";
import { Label } from "reactstrap";
import { toast } from "react-toastify";
import axiosClient from "../../../axios-client";

const LcaRecomandModal = () => {
  const { selectedAd, recomandModal } = useSelector((state) => state.lcAds);
  const dispatch = useDispatch();
  const [allServices, setAllServices] = useState([]);
  const [service, setService] = useState([]);
  const [error, setError] = useState(null);

  const handleClose = () => {
    dispatch(setRecomandModal(false));
    setService([]);
    setError(null);
    dispatch(setSelectedAd(null));
  };

  useEffect(() => {
    if (recomandModal) {
      fetchServices();
    }
  }, [recomandModal]);

  const fetchServices = async () => {
    try {
      const bwvData = await fetchBWV();
      const filterActive = bwvData.filter((vendor) => vendor.isactive === "3");
      const filterByCat = filterActive.filter(
        (vendor) => vendor.categoryId === selectedAd?.categoryId
      );
      setAllServices(filterByCat);
      setError(null);
    } catch (err) {
      console.error("Error fetching services:", err);
      setError("Failed to load services. Please try again.");
    }
  };

  const options = useMemo(
    () =>
      allServices
        ?.map((list) => ({
          value: JSON.stringify(list),
          label: `${list.listName} ${
            list.FullName ? `(${list.FullName})` : ""
          }`,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)) || [],
    [allServices]
  );

  // Enhanced custom styles for react-select with multi-select support
  const customStyles = {
    control: (provided, state) => ({
      ...provided,
      minHeight: "40px",
      borderColor: state.isFocused ? "#3f51b5" : "#ced4da",
      boxShadow: state.isFocused ? "0 0 0 1px #3f51b5" : "none",
      "&:hover": {
        borderColor: state.isFocused ? "#3f51b5" : "#aaa",
      },
      backgroundColor: "#fff",
      fontSize: "16px",
      padding: "2px",
    }),
    menu: (provided) => ({
      ...provided,
      zIndex: 9999,
      borderRadius: "4px",
      boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
      backgroundColor: "#fff",
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected
        ? "#3f51b5"
        : state.isFocused
        ? "#f5f5f5"
        : "#fff",
      color: state.isSelected ? "#fff" : "#333",
      padding: "10px 12px",
      fontSize: "16px",
      "&:hover": {
        backgroundColor: "#f5f5f5",
      },
    }),
    placeholder: (provided) => ({
      ...provided,
      color: "#999",
      fontSize: "16px",
    }),
    singleValue: (provided) => ({
      ...provided,
      color: "#333",
      fontSize: "16px",
    }),
    multiValue: (provided) => ({
      ...provided,
      backgroundColor: "#e8eaf6",
      borderRadius: "4px",
      fontSize: "14px",
    }),
    multiValueLabel: (provided) => ({
      ...provided,
      color: "#3f51b5",
      padding: "2px 6px",
    }),
    multiValueRemove: (provided) => ({
      ...provided,
      color: "#3f51b5",
      cursor: "pointer",
      "&:hover": {
        backgroundColor: "#c5cae9",
        color: "#fff",
      },
    }),
    menuPortal: (provided) => ({
      ...provided,
      zIndex: 9999,
    }),
  };

  const handleSendRecommend = async () => {
    if (service.length === 0) {
      setError("Please select at least one service provider.");
      return;
    }

    // Extract unique createdBy IDs as numbers
    const userIds = [
      ...new Set(
        service
          .filter((s) => s.createdBy != null && !isNaN(Number(s.createdBy)))
          .map((s) => Number(s.createdBy))
      ),
    ];

    if (userIds.length === 0) {
      setError("No valid provider IDs selected.");
      return;
    }

    const request = {
      userIds,
      needId: selectedAd?.Id,
    };

    try {
      console.log("Sending userIds:", userIds); // Debug log
      const response = await axiosClient.post(
        `message/recommendVendor`,
        request
      );
      if (response.data.statusCode === 200) {
        toast.success("Recommendation sent successfully");
        handleClose();
      } else {
        toast.error(response.data.msg || "Failed to send recommendation");
      }
    } catch (error) {
      toast.error("Error sending recommendation");
      console.error("API error:", error);
    }
  };

  return (
    <Dialog
      open={recomandModal}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      sx={{ "& .MuiDialog-paper": { padding: "20px", minHeight: "200px" } }}
    >
      <DialogContent>
        <div>
          <Label for="provider">Providers</Label>
          <Select
            options={options}
            onChange={(selectedOptions) =>
              setService(
                selectedOptions
                  ? selectedOptions.map((option) => JSON.parse(option.value))
                  : []
              )
            }
            placeholder="Select Service Providers"
            isSearchable
            isMulti
            closeMenuOnSelect={false}
            styles={customStyles}
            className="react-select-container"
            classNamePrefix="react-select"
            menuPortalTarget={document.body}
            noOptionsMessage={() =>
              error || options.length === 0
                ? "No providers available"
                : "No matching providers"
            }
            value={options.filter((option) =>
              service.some((s) => JSON.stringify(s) === option.value)
            )}
          />
          {error && <p style={{ color: "red", marginTop: "10px" }}>{error}</p>}
        </div>
      </DialogContent>
      <DialogActions sx={{ display: "flex", justifyContent: "flex-end" }}>
        <Button onClick={handleClose} color="primary">
          Cancel
        </Button>
        <Button onClick={handleSendRecommend} color="primary" autoFocus>
          Recommend
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default LcaRecomandModal;
